import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { InfoButton } from '../connectedForm/Button';
import notificationActionMutation from '../../graphql/mutations/actions/NotificationActionMutation.graphql';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import CustomerPriceMatrixModal from '../modals/CustomerPriceMatrixModal';
import { useProcessAlerts } from '../../utils/EntityMetaDataUtil';
import getClientMutationId from '../../utils/getClientMutationId';
import {
  Maybe,
  IEntityMetaData,
  INotificationAction,
  INotificationActionMutationResult,
  INotificationActionMutationVariables,
} from '../../graphql/schema/p4types';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import Notification, { INotificationProps } from './Notification';

const getActionMutationOptions = (notificationData: INotificationAction, selectedOption: Maybe<string>) => {
  return {
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        action: normalizeVariables({ ...notificationData, selectedOption: selectedOption }),
      },
    },
  };
};

export default ({
  type,
  title,
  message,
  dismiss,
  notificationData,
}: INotificationProps & { dismiss: () => void; notificationData: INotificationAction }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleQueryError = useComponentQueryErrorHandling();
  const processAlerts = useProcessAlerts();
  const [callMutation] = useMutation<INotificationActionMutationResult, INotificationActionMutationVariables>(
    notificationActionMutation,
    {
      onError: handleQueryError,
      onCompleted: (response) => {
        //Scan for modal/action matches - Do not navigate back if found.
        const actions = response?.notificationActionMutation?.result?.entityMetaData?.actions || [];
        if (actions.find((action) => action?.actionType === 'NOTIFICATION_ACTION_MODAL')) {
          setModalOpen(true);
          return;
        }

        //Check response for further alerts (Typically expecting a success notification, but could involve further actions, etc).
        processAlerts(response?.notificationActionMutation?.result?.entityMetaData as IEntityMetaData, title);
        dismiss();
      },
    }
  );

  const options = notificationData.options ?? [];

  return (
    <>
      {modalOpen ? (
        <CustomerPriceMatrixModal onDismiss={dismiss} notification={notificationData} modalOpen={modalOpen} />
      ) : (
        <Notification
          type={type}
          title={title}
          message={message}
          actionButtons={options.map((option) => {
            return (
              <InfoButton
                key={option!}
                onClick={() => {
                  callMutation(getActionMutationOptions(notificationData, option));
                }}
              >
                {option}
              </InfoButton>
            );
          })}
        />
      )}
    </>
  );
};
